import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { type ParameterItem, ParameterPicker } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { createParameterCategoriesFromData } from "components/CC/UI/ParameterPicker/parameterUtils.tsx";
import { DisabledTagDisplay } from "components/CC/UI/TagInput/DisabledTagDisplay";
import { FunctionPillNode } from "components/CC/UI/TagInput/FunctionPillNode";
import { TagDragDropPlugin } from "components/CC/UI/TagInput/TagDragDropPlugin";
import {
    BraceTransformPlugin,
    EditorRefPlugin,
    FunctionCallTransformPlugin,
    InitialValuePlugin,
    InsertRawTextCommandPlugin,
    InsertTagCommandPlugin,
    TagValidationPlugin,
} from "components/CC/UI/TagInput/tagInputPlugins";
import {
    INSERT_RAW_TEXT_COMMAND,
    INSERT_TAG_COMMAND,
    type TagInputMode,
    type TagTextInputProps,
} from "components/CC/UI/TagInput/tagInputTypes";
import {
    buildDisplayLabelLookup,
    buildNodeTypeLookup,
    buildPrefixValidationLookup,
    buildStepLabelLookup,
    buildStepNodeTypeLookup,
    buildValidationLookup,
    focusEditorAtEnd,
    getEffectiveMaxTags,
    getRawTextInsertPayload,
    insertTagOnlyPlaceholder,
    isEditorEmpty,
    isUntouchedTagOnlyPlaceholder,
    loadValueIntoEditor,
    normalizeValueByMode,
    resolveFocusableTarget,
    resolveTargetElement,
    resolveTagValidation,
    serializeEditorState,
} from "components/CC/UI/TagInput/tagInputUtils";
import { $isTagNode, TagNode } from "components/CC/UI/TagInput/TagNode";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import {
    $createParagraphNode,
    $createTextNode,
    $getNearestNodeFromDOMNode,
    $getRoot,
    $isElementNode,
    $isTextNode,
    $setSelection,
    type LexicalEditor,
} from "lexical";
import React, { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";

// Re-export so existing importers of INSERT_TAG_COMMAND from this file continue to work.
export { INSERT_TAG_COMMAND } from "components/CC/UI/TagInput/tagInputTypes";

// ============================================================================
// Inner components (must live inside LexicalComposer)
// ============================================================================

interface EditorContentProps {
    placeholder: string;
    onChange?: (value: string) => void;
    mode: TagInputMode;
    maxTags?: number;
    onStrictRuleViolation?: () => void;
    onStrictRuleClear?: () => void;
}

const EditorContent = ({
    placeholder,
    onChange,
    mode,
    maxTags,
    onStrictRuleViolation,
    onStrictRuleClear,
}: EditorContentProps): React.JSX.Element => {
    const [editor] = useLexicalComposerContext();

    const replaceSingleTagWithText = useCallback(
        (text: string) => {
            editor.update(() => {
                const root = $getRoot();
                root.clear();
                const paragraph = $createParagraphNode();
                paragraph.append($createTextNode(text));
                root.append(paragraph);
                paragraph.selectEnd();
            });
            onStrictRuleClear?.();
        },
        [editor, onStrictRuleClear]
    );

    const shouldReplaceSingleTag = useCallback((): boolean => {
        let replace = false;
        editor.getEditorState().read(() => {
            const root = $getRoot();
            const paragraphs = root.getChildren();
            if (paragraphs.length !== 1) return;
            const paragraph = paragraphs[0];
            if (!$isElementNode(paragraph)) return;
            const children = paragraph.getChildren();
            replace = children.length === 1 && $isTagNode(children[0]);
        });
        return replace;
    }, [editor]);

    return (
        <div className="flex-1 min-w-0 relative">
            <PlainTextPlugin
                contentEditable={
                    <ContentEditable
                        data-tag-editor-root="true"
                        className="outline-none text-sm min-h-[22px] leading-6 py-0.5"
                        aria-label={placeholder}
                        onBeforeInput={(e) => {
                            const nativeData = (e.nativeEvent as InputEvent).data;
                            if (mode === "strict-single" && nativeData === "{") {
                                const target = e.currentTarget as HTMLElement;
                                const text = target.textContent?.trim() ?? "";
                                if (text && text !== "{") {
                                    e.preventDefault();
                                    onStrictRuleViolation?.();
                                }
                            }
                        }}
                        onPaste={() => {
                            // tag-only: paste allowed — normalization strips non-tag text after transform
                        }}
                        onKeyDown={(e) => {
                            if (
                                mode === "strict-single" &&
                                e.key.length === 1 &&
                                !e.ctrlKey &&
                                !e.metaKey &&
                                !e.altKey
                            ) {
                                if (shouldReplaceSingleTag()) {
                                    e.preventDefault();
                                    replaceSingleTagWithText(e.key);
                                    return;
                                }
                            }
                        }}
                    />
                }
                placeholder={
                    <div className="absolute top-0 left-0 right-0 truncate text-sm text-gray-400 pointer-events-none py-0.5 leading-6">
                        {placeholder}
                    </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin
                onChange={(editorState, editor) => {
                    const serialized = serializeEditorState(editorState);
                    const normalized = normalizeValueByMode(serialized, mode, maxTags);
                    if (normalized !== serialized) {
                        loadValueIntoEditor(editor, normalized);
                    }
                    if (mode === "strict-single") {
                        const hasTag = /\{[^{}]+\}/.test(normalized);
                        const isSingleTagOnly = /^\s*\{[^{}]+\}\s*$/.test(normalized);
                        if (hasTag && !isSingleTagOnly) {
                            onStrictRuleViolation?.();
                        } else {
                            onStrictRuleClear?.();
                        }
                    }
                    // The untouched tag-only "{}" scaffold is a local editing aid, not real content.
                    const reported = mode === "tag-only" && normalized === "{}" ? "" : normalized;
                    onChange?.(reported);
                }}
                ignoreSelectionChange
            />
        </div>
    );
};

// ============================================================================
// TagTextInput
// ============================================================================

export const TagTextInput = ({
    value = "",
    onChange,
    placeholder,
    categories: categoriesProp,
    formFields,
    customFields,
    disabled = false,
    mode = "free",
    maxTags,
    onDebugTagsChange,
    className,
}: TagTextInputProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const [isFocused, setIsFocused] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const activeRef = useRef(false);
    const instanceIdRef = useRef(Math.random().toString(36).slice(2));
    const debugIdRef = useRef(`TagInput-${Math.random().toString(36).slice(2)}`);

    const focusedRef = useRef(false);
    const pickerOpenRef = useRef(false);

    useEffect(() => {
        activeRef.current = isFocused || isPickerOpen;
        focusedRef.current = isFocused;
        pickerOpenRef.current = isPickerOpen;
    }, [isFocused, isPickerOpen]);

    // Sync isFocused with actual DOM focus state via native focus/blur events
    useEffect(() => {
        const editor = editorRef.current?.getRootElement();
        if (!editor) return;

        const handleEditorFocus = () => setIsFocused(true);
        const handleEditorBlur = () => {
            if (!pickerOpenRef.current) {
                setIsFocused(false);
            }
        };

        editor.addEventListener("focus", handleEditorFocus);
        editor.addEventListener("blur", handleEditorBlur);

        return () => {
            editor.removeEventListener("focus", handleEditorFocus);
            editor.removeEventListener("blur", handleEditorBlur);
        };
    }, []);
    const [strictRuleWarning, setStrictRuleWarning] = useState(false);
    const editorRef = useRef<LexicalEditor | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pickerSuppressedRef = useRef(false);
    // Tracks whether the most recent pointerdown landed inside the open popover — set
    // synchronously in the capture-phase pointerdown handler below, before any blur/focusout
    // it triggers can run. Lets handleFocusOut tell "click inside popover" apart from a real
    // outside click, since a click on non-focusable popover content moves activeElement to
    // <body>, which looks identical to a genuine outside click if judged by activeElement alone.
    const lastPointerDownInPopoverRef = useRef(false);

    const resolvedPlaceholder = placeholder ?? t("parameter_picker.input_placeholder");

    const categories = useMemo(() => {
        if (categoriesProp && categoriesProp.length > 0) return categoriesProp;
        if (formFields || customFields) return createParameterCategoriesFromData({ formFields, customFields }, t);
        return [];
    }, [categoriesProp, formFields, customFields, t]);
    const validationLookup = useMemo(() => buildValidationLookup(categories), [categories]);
    const prefixValidationLookup = useMemo(() => buildPrefixValidationLookup(categories), [categories]);
    const displayLabelLookup = useMemo(() => buildDisplayLabelLookup(categories), [categories]);
    const stepLabelLookup = useMemo(() => buildStepLabelLookup(categories), [categories]);
    const nodeTypeLookup = useMemo(() => buildNodeTypeLookup(categories), [categories]);
    const stepNodeTypeLookup = useMemo(() => buildStepNodeTypeLookup(categories), [categories]);

    const normalizedValue = useMemo(() => normalizeValueByMode(value, mode, maxTags), [value, mode, maxTags]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleFocusIn = () => {
            // Must run before the picker-suppressed/already-open guards below — a tag removal
            // re-focuses the field while its picker is already open, which those guards skip.
            const editor = editorRef.current;
            if (editor && mode === "tag-only" && isEditorEmpty(editor)) {
                insertTagOnlyPlaceholder(editor);
            }

            // If another input just took focus, close picker and don't dispatch event
            if (pickerSuppressedRef.current) {
                pickerSuppressedRef.current = false; // Reset the flag
                setIsFocused(false);
                setIsPickerOpen(false);
                return;
            }

            // If picker is already open in THIS input, don't dispatch event again
            if (pickerOpenRef.current) {
                return;
            }

            setIsFocused(true);
            document.dispatchEvent(new CustomEvent("tag-input-focused", { detail: { id: instanceIdRef.current } }));
            setIsPickerOpen(true);
        };
        const convertRawTagsIfNeeded = (editor: LexicalEditor) => {
            let hasRawTagText = false;
            editor.getEditorState().read(() => {
                for (const para of $getRoot().getChildren()) {
                    if (!$isElementNode(para)) continue;
                    for (const child of para.getChildren()) {
                        if ($isTextNode(child) && child.getTextContent().includes("{")) {
                            hasRawTagText = true;
                        }
                    }
                }
            });
            if (hasRawTagText) {
                loadValueIntoEditor(editor, serializeEditorState(editor.getEditorState()));
            }
        };

        const closeAndConvert = () => {
            setIsFocused(false);
            setIsPickerOpen(false);
            // Mark as suppressed to prevent focusin from re-opening picker immediately
            pickerSuppressedRef.current = true;
            window.setTimeout(() => {
                pickerSuppressedRef.current = false;
            }, 50);
            const editor = editorRef.current;
            if (!editor) return;
            if (mode === "tag-only" && isUntouchedTagOnlyPlaceholder(editor)) {
                editor.update(() => {
                    $getRoot().clear();
                    $getRoot().append($createParagraphNode());
                });
                return;
            }
            convertRawTagsIfNeeded(editor);
        };

        const handleFocusOut = () => {
            window.setTimeout(() => {
                // A click on a non-focusable area inside the open popover (e.g. its header
                // background) blurs the editor without moving focus into the popover — the
                // browser just drops focus to <body>. That's not an outside click; the
                // pointerdown handler below already knows whether the interaction started
                // inside the popover, so defer to it instead of trusting activeElement alone.
                if (lastPointerDownInPopoverRef.current) {
                    lastPointerDownInPopoverRef.current = false;
                    return;
                }

                const active = document.activeElement;
                const inPopover = active instanceof HTMLElement && Boolean(active.closest(".ant-popover"));
                if (!container?.contains(active) && !inPopover) {
                    closeAndConvert();
                }
            }, 0);
        };

        // Close the picker when keyboard focus moves from the popover to something outside both
        // the container and the popover (handleFocusOut only covers container→outside transitions).
        const handleDocumentFocusIn = (e: FocusEvent) => {
            if (!activeRef.current) return;
            const target = e.target;
            if (!(target instanceof HTMLElement)) return;
            if (container.contains(target)) return;
            if (target.closest(".ant-popover")) return;
            closeAndConvert();
        };

        container.addEventListener("focusin", handleFocusIn);
        container.addEventListener("focusout", handleFocusOut);
        document.addEventListener("focusin", handleDocumentFocusIn, true);
        return () => {
            container.removeEventListener("focusin", handleFocusIn);
            container.removeEventListener("focusout", handleFocusOut);
            document.removeEventListener("focusin", handleDocumentFocusIn, true);
        };
    }, []);

    // Content-driven fallback for handleFocusIn: catches removals that don't trigger a
    // blur/refocus cycle (e.g. keyboard backspace while already focused).
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor || mode !== "tag-only") return;

        const isFieldActive = (): boolean => {
            const container = containerRef.current;
            const active = document.activeElement;
            if (!container || !active) return false;
            if (container.contains(active)) return true;
            return active instanceof HTMLElement && Boolean(active.closest(".ant-popover"));
        };

        return editor.registerUpdateListener(() => {
            if (!isFieldActive()) return;
            if (!isEditorEmpty(editor)) return;
            insertTagOnlyPlaceholder(editor);
        });
    }, [mode]);

    useEffect(() => {
        const handlePointerDownOutside = (event: PointerEvent) => {
            const container = containerRef.current;
            const target = event.target as Node | null;
            const targetElement = resolveTargetElement(event.target);
            lastPointerDownInPopoverRef.current = false;
            if (!container || !target) return;

            if (container.contains(target)) return;
            if (targetElement?.closest(".ant-popover")) {
                lastPointerDownInPopoverRef.current = true;
                return;
            }

            const editor = editorRef.current;
            const editorRoot = editor?.getRootElement();
            const activeElement = document.activeElement;
            const wasActiveEditor =
                Boolean(editorRoot) &&
                (activeElement === editorRoot || (activeElement instanceof Node && container.contains(activeElement)));

            if (!wasActiveEditor) {
                setIsFocused(false);
                setIsPickerOpen(false);
                return;
            }

            // Do not blur the current active element during capture phase; that can
            // interrupt native focus transfer to the clicked control. Instead, wait
            // until the browser finishes the click/focus sequence, then release the
            // Lexical root if it is still active.
            window.setTimeout(() => {
                const focusTarget = resolveFocusableTarget(event.target);

                if (editor) {
                    editor.update(() => {
                        $setSelection(null);
                    });
                    editor.blur();
                }

                if (focusTarget && document.activeElement !== focusTarget) {
                    focusTarget.focus();
                }

                setIsFocused(false);
                setIsPickerOpen(false);
            }, 0);
        };

        document.addEventListener("pointerdown", handlePointerDownOutside, true);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDownOutside, true);
        };
    }, [debugIdRef]);

    // Close this picker when another TagTextInput opens its own.
    useEffect(() => {
        const id = instanceIdRef.current;
        const handler = (e: Event) => {
            const otherId = (e as CustomEvent<{ id: string }>).detail.id;
            if (otherId !== id && activeRef.current) {
                // Mark that this active input now should suppress - another input took focus
                pickerSuppressedRef.current = true;
                setIsFocused(false);
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("tag-input-focused", handler);
        return () => document.removeEventListener("tag-input-focused", handler);
    }, [debugIdRef]);

    const handleParameterSelect = useCallback(
        (selectValue: string, item: ParameterItem) => {
            const editor = editorRef.current;
            if (!editor) return;
            const replaceAll =
                mode === "strict-single" ||
                (mode === "tag-only" && getEffectiveMaxTags(mode, maxTags) === 1) ||
                (mode === "tag-only" && isUntouchedTagOnlyPlaceholder(editor));

            const rawTextPayload = getRawTextInsertPayload(selectValue, item);
            if (rawTextPayload) {
                editor.dispatchCommand(INSERT_RAW_TEXT_COMMAND, { ...rawTextPayload, replaceAll });
                return;
            }

            const inner =
                selectValue.startsWith("{") && selectValue.endsWith("}") ? selectValue.slice(1, -1) : selectValue;
            const resolved = resolveTagValidation(inner, validationLookup, prefixValidationLookup);
            editor.dispatchCommand(INSERT_TAG_COMMAND, {
                inner,
                paramType: item.paramType ?? resolved.paramType,
                // Also replace-all on an untouched "{}" placeholder so the pick fills the braces
                // instead of splitting them into "{" + tag + "}".
                replaceAll,
                displayLabel: item.displayLabel,
                nodeType: item.nodeType,
            });
        },
        [validationLookup, prefixValidationLookup, mode, maxTags]
    );

    const handleContainerMouseDownCapture = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const target = event.target as HTMLElement | null;
            const container = containerRef.current;
            const editor = editorRef.current;
            const editorRoot = editor?.getRootElement();

            if (!target || !container || !editor || !editorRoot) return;
            if (target.closest("button")) return;
            if (target.closest(".ant-popover")) return;

            const clickedPillElement = target.closest<HTMLElement>(
                "[data-tag-node='true'], [data-function-pill='true']"
            );
            const clickedTagNode = Boolean(clickedPillElement);
            const clickedContainer = target === container;
            const clickedInsideEditor = editorRoot.contains(target);
            const isAlreadyActive = document.activeElement === editorRoot;
            const clickX = event.clientX;

            if (!clickedTagNode && !clickedContainer && !clickedInsideEditor) return;
            if (isAlreadyActive && clickedInsideEditor && !clickedTagNode) return;

            window.requestAnimationFrame(() => {
                const currentEditor = editorRef.current;
                if (!currentEditor) return;
                // Runs after handleFocusIn's insertion, so it's the final word on caret position.
                if (
                    mode === "tag-only" &&
                    (isEditorEmpty(currentEditor) || isUntouchedTagOnlyPlaceholder(currentEditor))
                ) {
                    insertTagOnlyPlaceholder(currentEditor);
                    currentEditor.focus();
                    return;
                }
                // Pills sit directly adjacent to zero-width gap markers with no pixel-width click
                // target of their own, so native hit-testing at a pill boundary is unreliable —
                // compute intent from which half of the clicked pill's own box was clicked instead.
                if (clickedPillElement?.isConnected) {
                    const rect = clickedPillElement.getBoundingClientRect();
                    const clickedLeftHalf = clickX < rect.left + rect.width / 2;
                    currentEditor.update(() => {
                        const node = $getNearestNodeFromDOMNode(clickedPillElement);
                        if (!node) return;
                        if (clickedLeftHalf) {
                            node.selectPrevious();
                        } else {
                            node.selectNext();
                        }
                    });
                    currentEditor.focus();
                    return;
                }
                // A click directly on editable text already placed the native caret at that
                // position — preserve it instead of jumping to the end.
                if (clickedInsideEditor && !clickedTagNode) {
                    currentEditor.focus();
                    return;
                }
                focusEditorAtEnd(currentEditor);
            });
        },
        [mode]
    );

    const initialConfig = useMemo(
        () => ({
            namespace: "TagTextInput",
            nodes: [TagNode, FunctionPillNode],
            onError: (error: Error) => {
                throw error;
            },
            theme: {},
        }),
        []
    );

    if (disabled) {
        return (
            <DisabledTagDisplay
                value={normalizedValue}
                placeholder={resolvedPlaceholder}
                displayLabelLookup={displayLabelLookup}
                stepLabelLookup={stepLabelLookup}
                validationLookup={validationLookup}
            />
        );
    }

    return (
        <ParameterPicker
            categories={categories}
            onSelect={handleParameterSelect}
            open={isPickerOpen}
            onOpenChange={(open) => {
                if (!open) pickerSuppressedRef.current = true;
                setIsPickerOpen(open);
            }}
            trigger={[]}
            disabled={disabled || categories.length === 0}
        >
            <div className="space-y-1">
                <div
                    ref={containerRef}
                    data-tag-text-input-container="true"
                    onMouseDownCapture={handleContainerMouseDownCapture}
                    onClick={() => {
                        pickerSuppressedRef.current = false;
                        document.dispatchEvent(
                            new CustomEvent("tag-input-focused", { detail: { id: instanceIdRef.current } })
                        );
                        setIsPickerOpen(true);
                    }}
                    className={`flex items-start gap-2 px-3 py-0 rounded border bg-white ${
                        isFocused ? "border-blue-400 ring-1 ring-blue-400" : "border-gray-300 hover:border-gray-400"
                    } ${className ?? ""}`}
                >
                    <LexicalComposer initialConfig={initialConfig}>
                        <EditorRefPlugin editorRef={editorRef as MutableRefObject<LexicalEditor | null>} />
                        <BraceTransformPlugin
                            lookup={validationLookup}
                            prefixLookup={prefixValidationLookup}
                            mode={mode}
                            onStrictRuleViolation={() => setStrictRuleWarning(true)}
                        />
                        <FunctionCallTransformPlugin />
                        <InitialValuePlugin value={normalizedValue} mode={mode} />
                        <InsertTagCommandPlugin lookup={validationLookup} prefixLookup={prefixValidationLookup} />
                        <InitialValuePlugin value={normalizedValue} mode={mode} />
                        <InsertTagCommandPlugin lookup={validationLookup} prefixLookup={prefixValidationLookup} />
                        <InsertRawTextCommandPlugin />
                        <TagDragDropPlugin />
                        <TagValidationPlugin
                            lookup={validationLookup}
                            prefixLookup={prefixValidationLookup}
                            displayLabelLookup={displayLabelLookup}
                            stepLabelLookup={stepLabelLookup}
                            nodeTypeLookup={nodeTypeLookup}
                            stepNodeTypeLookup={stepNodeTypeLookup}
                            onDebugTagsChange={onDebugTagsChange}
                        />
                        <EditorContent
                            placeholder={resolvedPlaceholder}
                            onChange={onChange}
                            mode={mode}
                            maxTags={maxTags}
                            onStrictRuleViolation={() => setStrictRuleWarning(true)}
                            onStrictRuleClear={() => setStrictRuleWarning(false)}
                        />
                    </LexicalComposer>
                </div>
                {strictRuleWarning && mode === "strict-single" && (
                    <p className="text-xs text-amber-600">{t("strict_single_input.rule_warning")}</p>
                )}
            </div>
        </ParameterPicker>
    );
};

export default TagTextInput;
